import { ASSIGNMENT_TAG, CONCEPTUAL_LABEL_TAG, INSTANT_FEEDBACK_CONFIG_TAG } from './utils/tags'
import {
    normalizeAddFilesToAssignmentResponse,
    normalizeCreateTextAssignmentFromZipResponse,
    normalizeFetchAssignmentFiltersResponse,
    normalizeSearchAssignmentsResponse,
} from './utils/normalizers/assignment.normalizers'
import { actions as assignmentActions } from '../reducers/assignment.reducer'
import { ASSIGNMENT_TRIAL_ACCESS_TYPES } from '../../constants/assignment-trial-access-types'
import baseApi from './base.api'
import { isNil } from 'lodash'
import { assignmentsPersistActions } from '../slices/assignments.persist.slice'
import getConfig from '../../configs/config'
import { createSearchParams } from '../../utils/request.helper'
import { ASSIGNMENT_SORTING_TYPES } from '../../constants/sorting-types'
import TrainingModes from '../../constants/training-modes'

export const assignmentApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        fetchAssignments: builder.query({
            query: ({
                name,
                sort = ASSIGNMENT_SORTING_TYPES.NEW_TO_OLD.value,
                type,
                language,
                page,
                pageSize,
                organizationIds,
                courseIds,
                searchType = 1,
                ownerIds,
                permissionLevel,
            }) => {
                const params = createSearchParams({
                    sort_by: sort,
                    name,
                    page,
                    page_size: pageSize,
                    assignment_type: type,
                    language,
                    organization_used_ids: organizationIds,
                    course: courseIds,
                    search_type: searchType,
                    owner_ids: ownerIds,
                    permission_level: permissionLevel,
                })

                return {
                    url: '/assignments/get-assignments',
                    method: 'GET',
                    params,
                }
            },
            providesTags: result =>
                result
                    ? [
                          { type: ASSIGNMENT_TAG, id: 'LIST' },
                          ...result.assignments.map(assignment => ({ type: ASSIGNMENT_TAG, id: assignment.token })),
                      ]
                    : [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createAssignmentFromZip: builder.mutation({
            query: ({ file, name, type }) => {
                const formData = new FormData()
                formData.append('zip_file', file)
                formData.append('assignment_name', name)
                formData.append('assignment_type', type)

                return {
                    url: '/assignments-upload/upload-zip-cs',
                    method: 'POST',
                    body: formData,
                    baseURL: getConfig().uploadsApiBaseUrl,
                }
            },
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createTextAssignmentFromCsv: builder.mutation({
            query: ({ file, name, type, submission_type }) => {
                const formData = new FormData()
                if (file) formData.append('file', file)
                formData.append('assignment_name', name)
                formData.append('assignment_type', type)
                if (submission_type !== undefined) formData.append('submission_type', submission_type)

                return {
                    url: '/assignments-upload/upload-free-text',
                    method: 'POST',
                    body: formData,
                }
            },
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createTextAssignmentFromCsvOld: builder.mutation({
            query: ({ file, name, type }) => {
                const formData = new FormData()
                formData.append('csv_file', file)
                formData.append('assignment_name', name)
                formData.append('assignment_type', type)

                return {
                    url: '/assignments-upload/upload-csv-cs-text',
                    method: 'POST',
                    body: formData,
                }
            },
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createTextAssignmentFromZip: builder.mutation({
            query: ({ file, name, type, submission_type }) => {
                const formData = new FormData()
                if (file) formData.append('zip_file', file)
                formData.append('assignment_name', name)
                formData.append('assignment_type', type)
                if (submission_type !== undefined) formData.append('submission_type', submission_type)

                return {
                    url: '/assignments-upload/upload-zip-cs-text',
                    method: 'POST',
                    body: formData,
                }
            },
            transformResponse: normalizeCreateTextAssignmentFromZipResponse,
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createMba: builder.mutation({
            query: ({ versionId, name, targetUserId, trialAccess }) => ({
                url: `/assignments-upload/create-mba`,
                method: 'POST',
                body: {
                    version_id: versionId,
                    assignment_name: name,
                    target_user_id: targetUserId,
                    trial_access:
                        !trialAccess.type || ASSIGNMENT_TRIAL_ACCESS_TYPES.NONE.value === trialAccess.type
                            ? null
                            : {
                                  type: trialAccess.type,
                                  max_submissions: trialAccess.numberOfSubmissions,
                                  expiration_date: trialAccess.expirationDate,
                              },
                },
            }),
            invalidatesTags: (result, error, arg) =>
                arg.isCreatingForCurrentUser ? [{ type: ASSIGNMENT_TAG, id: 'LIST' }] : [],
        }),
        updateAssignmentSettings: builder.mutation({
            query: ({
                token,
                name,
                description,
                difficultyLevel,
                accessType,
                timeToSolveInMinutes,
                prerequisites,
                willLearn,
                sampleSubmissionIds,
                startDate,
                endDate,
                sendReasoningToStudents,
                showUnitTestTips,
                socraticChatEnabled,
                disciplineId,
                courseId,
                dispenserOn,
                feedbackPhilosophy,
                submissionType,
            }) => ({
                url: `/assignment/${token}`,
                method: 'PATCH',
                body: {
                    assignment_name: name,
                    description,
                    difficulty_level: difficultyLevel,
                    access_type: accessType,
                    time_to_solve: timeToSolveInMinutes,
                    prerequisites,
                    will_learn: willLearn,
                    sample_submissions: sampleSubmissionIds,
                    start_date: startDate,
                    end_date: endDate,
                    send_explainability_feedback: sendReasoningToStudents,
                    llm_ut_explanation: showUnitTestTips,
                    socratic_chat_enabled: socraticChatEnabled,
                    discipline_id: disciplineId,
                    course_id: courseId,
                    dispenser_on: dispenserOn,
                    philosophy_text: feedbackPhilosophy,
                    submission_type: submissionType,
                },
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'LIST' }],
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => dispatch(assignmentActions.fetchAssignmentInfo(arg.token)))
            },
        }),
        addFilesToAssignment: builder.mutation({
            query: ({ token, file }) => {
                const formData = new FormData()
                formData.append('file', file)

                return {
                    url: `/assignments-upload/${token}/add-files`,
                    method: 'POST',
                    body: formData,
                }
            },
            transformResponse: normalizeAddFilesToAssignmentResponse,
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => dispatch(assignmentActions.fetchAssignmentInfo(arg.token)))
            },
        }),
        fetchAssignmentMbas: builder.query({
            query: ({ token }) => ({
                url: `/assignment/${token}/mbas`,
                method: 'GET',
            }),
        }),
        cloneAssignment: builder.mutation({
            query: ({ token, name, userId, includeMbas = false }) => ({
                url: `/assignments-upload/${token}/copy`,
                method: 'POST',
                body: { assignment_name: name, user_id: userId, forward_mbas: includeMbas },
            }),
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createAlertReview: builder.mutation({
            query: ({ token, email, resolution }) => ({
                url: `/assignment-metadata/${token}/update-alert-suppression`,
                method: 'PUT',
                body: { reviewer: email, review_resolution: resolution },
            }),
        }),
        fetchModels: builder.query({
            query: () => ({
                url: `/assignments/get-models`,
                method: 'GET',
            }),
        }),
        searchAssignments: builder.query({
            query: ({ name }) => ({
                url: `/assignments/search`,
                method: 'GET',
                params: { name, limit: 5 },
            }),
            transformResponse: normalizeSearchAssignmentsResponse,
        }),
        fetchAssignmentFilters: builder.query({
            query: ({ searchType }) => ({
                url: `/assignments/filters`,
                method: 'GET',
                params: searchType ? { search_type: searchType } : undefined,
            }),
            providesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
            transformResponse: normalizeFetchAssignmentFiltersResponse,
        }),
        trainAndProjectTextModel: builder.mutation({
            query: ({ token, trainingMode = TrainingModes.ML_LLM }) => ({
                url: `/text-models/${token}/train-and-project`,
                method: 'POST',
                params: { training_mode: trainingMode },
            }),
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => dispatch(assignmentActions.fetchAssignmentInfo(arg.token)))
            },
            invalidatesTags: (result, error, arg) => [{ type: ASSIGNMENT_TAG, id: arg.token }],
        }),
        findInconsistenciesInTextModel: builder.mutation({
            query: ({ token }) => ({
                url: `/text-models/${token}/find-inconsistencies`,
                method: 'POST',
            }),
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => {
                    dispatch(assignmentActions.fetchAssignmentInfo(arg.token))
                    dispatch(assignmentsPersistActions.setConsistencyCheckCompleted(true))
                })
            },
        }),
        exportModel: builder.query({
            query: ({ token }) => ({
                url: `/assignments-upload/${token}/export-assignment`,
                baseURL: getConfig().uploadsApiBaseUrl,
                method: 'GET',
                body: {
                    response_format: 'zip',
                },
                responseType: 'blob',
            }),
        }),
        exportMatches: builder.query({
            query: ({ token }) => ({
                url: `/assignments-upload/${token}/export-pattern-matches`,
                method: 'GET',
                body: {
                    response_format: 'csv',
                },
                responseType: 'blob',
            }),
        }),
        exportPatternGrades: builder.query({
            query: ({ token }) => ({
                url: `/assignments-upload/${token}/export-pattern-grades`,
                method: 'GET',
            }),
        }),
        importModel: builder.mutation({
            query: ({ file }) => {
                const formData = new FormData()
                formData.append('file', file)

                return {
                    url: `/assignments-upload/import-assignment`,
                    baseURL: getConfig().uploadsApiBaseUrl,
                    method: 'POST',
                    body: formData,
                }
            },
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        downloadNewTextSubmissions: builder.query({
            query: ({ token, exportMode, testSetOnly }) => ({
                url: `/assignment/${token}/download-submissions`,
                method: 'GET',
                body: {
                    response_format: 'csv',
                },
                params: {
                    export_mode: exportMode,
                    test_set_only: testSetOnly,
                },
                responseType: 'blob',
            }),
        }),
        exportWorksheet: builder.query({
            query: ({ token, docFormat }) => ({
                url: `/questions/${token}/export-template`,
                method: 'GET',
                params: {
                    doc_format: docFormat,
                },
                responseType: 'blob',
            }),
        }),
        exportPersonalizedFeedback: builder.query({
            query: ({ token }) => ({
                url: `/assignments-upload/${token}/export-student-feedbacks`,
                method: 'GET',
                body: {
                    response_format: 'json',
                },
                responseType: 'blob',
            }),
        }),
        exportStudentGradingResult: builder.query({
            query: ({ token }) => ({
                url: `/text-models/${token}/export-student-grading-result`,
                method: 'GET',
                responseType: 'blob',
            }),
        }),
        exportEscalatedSubmissions: builder.query({
            query: ({ token, includeAllHistory }) => ({
                url: `/assignments-upload/${token}/export-escalated-submissions`,
                method: 'GET',
                params: {
                    include_all_history: includeAllHistory,
                },
                responseType: 'blob',
            }),
        }),
        resetProcessingStatus: builder.mutation({
            query: ({ token }) => ({
                url: `/assignment/${token}/reset-processing-status`,
                method: 'GET',
            }),
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => dispatch(assignmentActions.fetchAssignmentInfo(arg.token)))
            },
            invalidatesTags: (result, error, arg) => [{ type: ASSIGNMENT_TAG, id: arg.token }],
        }),
        fetchPrioritizer: builder.query({
            query: ({ token }) => ({
                url: `/assignment-metadata/${token}/get-prioritizer`,
                method: 'GET',
            }),
        }),
        updatePrioritizer: builder.mutation({
            query: ({ token, philosophyText, dispenserOn }) => ({
                url: `/assignment-metadata/${token}/update-prioritizer`,
                method: 'PUT',
                body: {
                    philosophy_text: philosophyText,
                    dispenser_on: dispenserOn,
                },
            }),
            onQueryStarted: async ({ token, philosophyText, dispenserOn }, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    dispatch(
                        baseApi.util.updateQueryData('fetchPrioritizer', { token }, draft => {
                            draft.philosophy_text = philosophyText
                            draft.dispenser_on = dispenserOn
                        })
                    )
                } catch {
                    /* mutation failed, no cache update needed */
                }
            },
        }),
        fetchInstantFeedbackConfig: builder.query({
            query: ({ token }) => ({
                url: `/assignment-metadata/${token}/instant-feedback-config`,
                method: 'GET',
            }),
            providesTags: (result, error, { token }) => [{ type: INSTANT_FEEDBACK_CONFIG_TAG, id: token }],
        }),
        updateInstantFeedbackConfig: builder.mutation({
            query: ({
                token,
                enabled,
                showGrades,
                requireHumanReview,
                submitForGrade,
                threshold,
                maxIterations,
                instructions,
            }) => ({
                url: `/assignment-metadata/${token}/instant-feedback-config`,
                method: 'PUT',
                body: {
                    enabled,
                    show_grades: showGrades,
                    require_human_review: requireHumanReview,
                    submit_for_grade: submitForGrade,
                    threshold,
                    max_iterations: maxIterations,
                    instructions,
                },
            }),
            invalidatesTags: (result, error, { token }) => [{ type: INSTANT_FEEDBACK_CONFIG_TAG, id: token }],
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => dispatch(assignmentActions.fetchAssignmentInfo(arg.token)))
            },
        }),
    }),
})

export const {
    useFetchAssignmentsQuery,
    useCreateAssignmentFromZipMutation,
    useCreateTextAssignmentFromCsvMutation,
    useCreateTextAssignmentFromCsvOldMutation,
    useCreateTextAssignmentFromZipMutation,
    useCreateMbaMutation,
    useUpdateAssignmentSettingsMutation,
    useAddFilesToAssignmentMutation,
    useFetchAssignmentMbasQuery,
    useCloneAssignmentMutation,
    useCreateAlertReviewMutation,
    useFetchModelsQuery,
    useSearchAssignmentsQuery,
    useFetchAssignmentFiltersQuery,
    useTrainAndProjectTextModelMutation,
    useFindInconsistenciesInTextModelMutation,
    useLazyExportModelQuery,
    useLazyExportMatchesQuery,
    useLazyExportPatternGradesQuery,
    useLazyExportWorksheetQuery,
    useImportModelMutation,
    useLazyDownloadNewTextSubmissionsQuery,
    useLazyExportPersonalizedFeedbackQuery,
    useLazyExportEscalatedSubmissionsQuery,
    useLazyExportStudentGradingResultQuery,
    useResetProcessingStatusMutation,
    useFetchPrioritizerQuery,
    useUpdatePrioritizerMutation,
    useFetchInstantFeedbackConfigQuery,
    useUpdateInstantFeedbackConfigMutation,
} = assignmentApi
